import React, { useMemo } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Box, Typography, Grid, Paper } from '@mui/material';
import * as types from 'application/_types/types.type';
import ToggleSwitch from 'application/components/ToggleSwitch/ToggleSwitch';
import FormSection from 'application/components/FormSection/FormSection';
import { IconMenuItem, IconSelect } from '../styledComponents/IconSelect';
import { getPinsDict } from 'application/routes/config/utils/tempsUtils';
import { GamifyIcon } from 'application/components/GamifyIcon';
import {
  StyledTextField,
  FieldLabel,
  SectionTitle,
  SwitchLabel,
  SwitchDescription,
  StyledButton,
  SecondaryButton,
  ErrorText,
} from './styledFormComponents';

const pinItems = getPinsDict();

const refactorIconName = (iconName: string) => {
  return (pinItems[iconName as keyof typeof pinItems] as string)
    .split('/')
    .pop()
    ?.split('-')
    .slice(0, -1)
    .join(' ');
};

interface PinSettingsFormProps {
  selectedCRM: types.StageCategoryPartial;
  disposition: types.StageDispositionPartial;
  onSubmit: (values: types.StageDispositionPartial) => void;
  onDelete: () => void;
}

export default function PinSettingsForm({
  disposition,
  onSubmit,
  onDelete,
  selectedCRM,
}: PinSettingsFormProps) {
  const initialValues: types.StageDispositionPartial = {
    id: disposition.id || '',
    title: disposition.title || '',
    isSetupDeal: disposition.isSetupDeal || false,
    noRecording: disposition.noRecording || false,
    isConversation: disposition.isConversation || false,
    isNotSelectable: disposition.isNotSelectable || false,
    iconName: disposition.iconName || '',
  };

  const usedPins = selectedCRM?.dispositions?.map((pin) => pin.iconName);

  const existingTitles = useMemo(
    () =>
      (selectedCRM?.dispositions ?? []).map((d) => ({
        id: d.id,
        title: (d.title || '').trim().toLowerCase(),
      })),
    [selectedCRM]
  );
  const ValidationSchema = useMemo(
    () =>
      Yup.object({
        title: Yup.string()
          .transform((v) => (v ?? '').trim()) // normalize for length/required + our test
          .required('Title is required')
          .max(20, 'Must be 20 characters or less')
          .test(
            'unique-title-in-category',
            'A pin with this title already exists in this category',
            function (value) {
              if (!value) return true;
              const normalized = value.trim().toLowerCase();
              // allow same title if it's the same disposition being edited
              const conflict = existingTitles.some(
                (t) => t.title === normalized && t.id !== disposition.id
              );
              return !conflict;
            }
          ),
        iconName: Yup.string().required('Icon is required'),
      }),
    [existingTitles, disposition.id]
  );

  const handleSubmit = (values: types.StageDispositionPartial) => {
    // Ensure trimmed title is submitted
    onSubmit({ ...values, title: (values.title || '').trim() });
  };

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({ backgroundColor: theme.custom?.background1 })}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          isValid,
          dirty,
          resetForm,
        }) => (
          <Form>
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                backgroundColor: theme.custom?.background1,
              })}
            >
              <FormSection
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <SectionTitle>
                  {disposition.id ? 'Pin Settings' : 'Create new Pin'}
                </SectionTitle>

                {/* Title */}
                <Box>
                  <FieldLabel sx={{ marginBottom: 1 }}>Title *</FieldLabel>
                  <Field
                    as={StyledTextField}
                    fullWidth
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.title && !!errors.title}
                    helperText={touched.title && errors.title}
                    inputProps={{ maxLength: 20 }}
                  />
                  <Typography
                    variant="caption"
                    sx={(theme) => ({ ml: 1, color: theme.custom?.textLight })}
                  >
                    {values.title?.length || 0}/20
                  </Typography>
                </Box>

                {/* Switches */}
                <FormSection
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ maxWidth: '80%' }}>
                    <SwitchLabel>Record as conversation</SwitchLabel>
                    <SwitchDescription>
                      This pin will be recorded as a conversation at the door
                    </SwitchDescription>
                  </Box>
                  <ToggleSwitch
                    checked={Boolean(values.isConversation)}
                    onChange={(checked: boolean) =>
                      setFieldValue('isConversation', checked, true)
                    }
                  />
                </FormSection>

                <FormSection
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ maxWidth: '80%' }}>
                    <SwitchLabel>Create Deal</SwitchLabel>
                    <SwitchDescription>
                      Dropping this pin will open a new deal form
                    </SwitchDescription>
                  </Box>
                  <ToggleSwitch
                    checked={Boolean(values.isSetupDeal)}
                    onChange={(checked: boolean) =>
                      setFieldValue('isSetupDeal', checked, true)
                    }
                  />
                </FormSection>

                <FormSection
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ maxWidth: '80%' }}>
                    <SwitchLabel>Not Selectable</SwitchLabel>
                    <SwitchDescription>
                      This pin will not be selectable to drop as initial pin
                    </SwitchDescription>
                  </Box>
                  <ToggleSwitch
                    checked={Boolean(values.isNotSelectable)}
                    onChange={(checked: boolean) =>
                      setFieldValue('isNotSelectable', checked, true)
                    }
                  />
                </FormSection>

                <FormSection
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ maxWidth: '80%' }}>
                    <SwitchLabel>Discard Recording</SwitchLabel>
                    <SwitchDescription>
                      Discard auto-generated recording when this pin is dropped
                    </SwitchDescription>
                  </Box>
                  <ToggleSwitch
                    checked={Boolean(values.noRecording)}
                    onChange={(checked: boolean) =>
                      setFieldValue('noRecording', checked, true)
                    }
                  />
                </FormSection>
              </FormSection>

              {/* Pin Icon */}
              <FormSection>
                <SectionTitle>Pin Icon</SectionTitle>
                <IconSelect
                  value={values.iconName}
                  onChange={(e: any) =>
                    setFieldValue('iconName', e.target.value)
                  }
                  MenuProps={{
                    PaperProps: {
                      sx: (theme) => ({
                        maxHeight: 240,
                        p: 0,
                        backgroundColor: theme.custom?.background1,
                        '& .MuiList-root': { p: 0 },
                      }),
                    },
                  }}
                >
                  {Object.keys(pinItems).map((key) => (
                    <IconMenuItem
                      key={key}
                      value={key}
                      disabled={
                        usedPins?.includes(key) &&
                        values.iconName !== key &&
                        disposition.iconName !== key
                      }
                    >
                      <Box
                        sx={{
                          width: '40px',
                          height: '40px',
                          overflow: 'hidden',
                          borderRadius: '4px',
                          marginRight: '8px',
                        }}
                      >
                        <img
                          src={pinItems[key as keyof typeof pinItems]}
                          alt={key}
                          style={{ width: '40px', height: '40px' }}
                        />
                      </Box>
                      <Typography
                        sx={(theme) => ({ color: theme.custom?.text })}
                      >
                        {refactorIconName(key)}
                      </Typography>
                    </IconMenuItem>
                  ))}
                </IconSelect>
                {errors.iconName && <ErrorText>{errors.iconName}</ErrorText>}
              </FormSection>

              {/* <ErrorText
                onClick={() => {
                  console.log('isSubmitting', isSubmitting);
                  console.log('isValid', isValid);
                  console.log('dirty', dirty);
                }}
              >
                {'submitterd'}
              </ErrorText>
              <ErrorText>{`isValid: ${isValid}`}</ErrorText>
              <ErrorText>{`dirty: ${dirty}`}</ErrorText> */}

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, marginTop: 3 }}>
                  <SecondaryButton
                    onClick={() => {
                      if (values.id) {
                        onDelete();
                      } else {
                        // reset the form
                        resetForm();
                      }
                    }}
                    disabled={disposition.isDefault}
                    sx={(theme) => ({
                      color: '#AE1414',
                      borderColor: '#AE1414',
                      '&:hover': {
                        backgroundColor: theme.custom?.isDark
                          ? 'rgba(174, 20, 20, 0.1)'
                          : 'rgba(174, 20, 20, 0.05)',
                      },
                    })}
                  >
                    <GamifyIcon color="inherit" icon="trash" />
                    <Typography pl={1} sx={{ color: 'inherit' }}>
                      {values.id ? 'Remove Pin' : 'Cancel'}
                    </Typography>
                  </SecondaryButton>
                  <StyledButton
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || !isValid || !dirty}
                  >
                    Save Changes
                  </StyledButton>
                </Box>
              </Grid>
            </Paper>
          </Form>
        )}
      </Formik>
    </Paper>
  );
}
